import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ActivatedRoute } from '@angular/router';
import { addMonths, format } from 'date-fns';
import { BehaviorSubject, map, Subject, switchMap, takeUntil } from 'rxjs';
import { PreviousJobs } from 'server/shared/collections/previousJobs';
import { EmploymentInvitee } from 'server/shared/models/employmentInvitee';
import { Invitee } from 'server/shared/models/invitee';
import { PartialAcceptTime } from 'server/shared/models/partialAcceptTime';
import { PreviousJob } from 'server/shared/models/previousJob';
import { PoolerUser } from 'server/shared/models/user';
import { dbDayDateToken } from 'server/static/dbDayDateToken';
import { PoolerService } from 'src/app/services/pooler/pooler.service';
import { observeTracker } from 'src/helpers/meteorObservable/observeTracker';
import { PreviousJobsEmploymentsLogSheetComponent } from '../previous-jobs-employments-log-sheet/previous-jobs-employments-log-sheet.component';
import { PreviousJobsLogSheetComponent } from '../previous-jobs-log-sheet/previous-jobs-log-sheet.component';

@Component({
  selector: 'app-previous-jobs-logs',
  templateUrl: './previous-jobs-logs.html',
  styleUrls: ['./previous-jobs-logs.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreviousJobsLogs implements OnInit, OnDestroy {
  destroyed$ = new Subject<void>();
  user$ = new BehaviorSubject<PoolerUser | undefined>(undefined);
  @Input() set user(value: PoolerUser | undefined) {
    this.user$.next(value);
  }
  get user() {
    return this.user$.value;
  }

  invitee$ = new BehaviorSubject<Invitee | EmploymentInvitee | undefined>(
    undefined,
  );
  @Input() set invitee(value: Invitee | EmploymentInvitee | undefined) {
    this.invitee$.next(value);
  }
  get invitee() {
    return this.invitee$.value;
  }

  departmentId$ = new BehaviorSubject<string>('');
  @Input() set departmentId(value: string) {
    this.departmentId$.next(value);
  }
  get departmentId() {
    return this.departmentId$.value;
  }

  partialAcceptTime$ = new BehaviorSubject<PartialAcceptTime | undefined>(
    undefined,
  );
  @Input() set partialAcceptTime(value: PartialAcceptTime | undefined) {
    this.partialAcceptTime$.next(value);
  }
  get partialAcceptTime() {
    return this.partialAcceptTime$.value;
  }

  jobsInDepartment$ = new BehaviorSubject<number>(0);
  jobsInOrganisation$ = new BehaviorSubject<number>(0);

  employmentsInDepartment$ = new BehaviorSubject<number>(0);
  employmentsInOrganisation$ = new BehaviorSubject<number>(0);

  previousJob$ = this.user$.pipe(
    switchMap((user) => {
      if (user) {
        return this.poolerService.previousJobsByUserId$.pipe(
          map((previousJobsByUserId) => previousJobsByUserId.get(user._id)),
        );
      } else {
        return observeTracker(() =>
          PreviousJobs.findOne({
            email: this.invitee$.value?.personalData.email,
            fullName:
              this.invitee$.value?.personalData.firstName +
              ' ' +
              this.invitee$.value?.personalData.lastName,
          }),
        );
      }
    }),
  );

  constructor(
    private route: ActivatedRoute,
    private bottomSheet: MatBottomSheet,
    private poolerService: PoolerService,
  ) {}

  ngOnInit(): void {
    this.previousJob$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((previousJob) => {
        this.setJobsInDepartment(previousJob);
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  setJobsInDepartment(previousJob: PreviousJob | undefined) {
    let jobsInOrganisation = 0;
    let jobsInDepartment = 0;
    let employmentsInOrganisation = 0;
    let employmentsInDepartment = 0;

    let organisation = previousJob?.organisations?.find((organisation) =>
      organisation.departments.some(
        (department) => department.id === this.departmentId$.value,
      ),
    );
    organisation?.departments?.forEach((department) => {
      const recentJobs = department.jobs.filter((job) => {
        return job.date > format(addMonths(new Date(), -3), dbDayDateToken);
      });
      const recentEmployments = department.employments.filter((employment) => {
        return (
          employment.endDate > format(addMonths(new Date(), -3), dbDayDateToken)
        );
      });
      if (department.id === this.departmentId$.value) {
        jobsInDepartment = recentJobs.length;
        employmentsInDepartment = recentEmployments.length;
      }
      jobsInOrganisation = jobsInOrganisation + recentJobs.length;
      employmentsInOrganisation =
        employmentsInOrganisation + recentEmployments.length;
    });

    this.jobsInOrganisation$.next(jobsInOrganisation);
    this.jobsInDepartment$.next(jobsInDepartment);
    this.employmentsInOrganisation$.next(employmentsInOrganisation);
    this.employmentsInDepartment$.next(employmentsInDepartment);
  }

  openPreviousJobsSheet(): void {
    this.route.params.subscribe(async (params) => {
      this.bottomSheet.dismiss();
    });
    this.bottomSheet.open(PreviousJobsLogSheetComponent, {
      data: {
        user: this.user$.value,
        email: this.invitee$.value?.personalData.email,
        name:
          this.invitee$.value?.personalData.firstName +
          ' ' +
          this.invitee$.value?.personalData.lastName,
        departmentId: this.departmentId$.value,
      },
    });
  }

  openPreviousJobsEmploymentsSheet(): void {
    this.route.params.subscribe(async (params) => {
      this.bottomSheet.dismiss();
    });
    this.bottomSheet.open(PreviousJobsEmploymentsLogSheetComponent, {
      data: {
        user: this.user$.value,
        email: this.invitee$.value?.personalData.email,
        name:
          this.invitee$.value?.personalData.firstName +
          ' ' +
          this.invitee$.value?.personalData.lastName,
        departmentId: this.departmentId$.value,
      },
    });
  }
}
